import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NEWS_TOPICS,
  planAtLeast,
  newsTopicLabel,
  normalizeNewsTopic,
  type NewsTopicId,
} from '@secretario/shared';
import {
  getSpecialistFeed,
  markSpecialistRead,
  saveNewsTopics,
  type PASpecialistFeedItem,
} from '../../../lib/api/team.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { NewsAlerts } from './NewsAlerts.js';
import { NewsProEdition } from './NewsProEdition.js';
import { useEntitlements } from '../../../lib/plan.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './noticias.css';

const FEED_KEY = ['pa-specialist-feed', 'noticias'];

function topicIcon(id: string) {
  return `/news-topics/${id}.webp`;
}

function spDay(iso: string) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date(iso));
}

function spTime(iso: string) {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDayLine(iso: string) {
  const day = new Date(iso).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  return `${day.charAt(0).toUpperCase()}${day.slice(1)}. Separei às ${spTime(iso)}.`;
}

/** Uma notícia em linha: arte do assunto, título, fonte e hora; abre no veículo. */
function NewsRow({ item, withSummary }: { item: PASpecialistFeedItem; withSummary: boolean }) {
  const meta = [item.source, spTime(item.createdAt)].filter(Boolean).join(' · ');
  return (
    <a className="pgs-row nv-row" href={item.url ?? undefined} target="_blank" rel="noreferrer">
      <span className="pgs-ic">
        <img src={topicIcon(item.theme ?? 'principais')} alt="" />
      </span>
      <span className="pgs-rowt">
        <b>{item.title}</b>
        {withSummary && item.summary && <span className="nv-summary">{item.summary}</span>}
        <small>{meta}</small>
      </span>
      <span className="nv-go" aria-hidden="true">
        ↗
      </span>
    </a>
  );
}

function TopicPicker({
  initial,
  editing,
  onSave,
  onCancel,
  onBack,
}: {
  initial: NewsTopicId[];
  editing: boolean;
  onSave: (topics: NewsTopicId[]) => void;
  onCancel: () => void;
  onBack: () => void;
}) {
  const [picked, setPicked] = useState<NewsTopicId[]>(initial);
  const toggle = (id: NewsTopicId) =>
    setPicked((cur) => (cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id]));

  return (
    <PageShell
      kicker="Notícias"
      title={editing ? 'Quais assuntos eu acompanho?' : 'O que merece espaço no seu dia?'}
      lead={
        editing
          ? 'Marque ou desmarque. Eu já separo as de hoje com os assuntos novos.'
          : 'Eu acompanho seus assuntos, corto o ruído e entrego o essencial para você começar o dia.'
      }
      onBack={editing ? onCancel : onBack}
    >
      <section className="pgs-section">
        <h2 className="pgs-h2">
          Assuntos <small>{picked.length}</small>
        </h2>
        <div className="pgs-chips">
          {NEWS_TOPICS.map((t) => {
            const on = picked.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                className={`pgs-chip nv-chip${on ? ' on' : ''}`}
                onClick={() => toggle(t.id)}
                aria-pressed={on}
              >
                <img src={topicIcon(t.id)} alt="" />
                {t.label}
              </button>
            );
          })}
        </div>
      </section>

      <div className="pgs-note">
        <i aria-hidden="true">i</i>
        <span>
          <b>Leio fontes abertas</b>
          <small>Resumo em português e deixo o link original de cada notícia.</small>
        </span>
      </div>

      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn solid"
          disabled={picked.length === 0}
          onClick={() => onSave(picked)}
        >
          {editing ? 'Salvar assuntos' : 'Pronto, pode acompanhar'}
        </button>
        {editing && (
          <button type="button" className="pgs-btn" onClick={onCancel}>
            Deixar como está
          </button>
        )}
      </div>
      {!editing && <p className="nv-fine">Dá pra mudar quando quiser.</p>}
    </PageShell>
  );
}

export function NoticiasView() {
  const back = useSkillBack();
  const qc = useQueryClient();
  // Enquanto o servidor separa as notícias de hoje, a tela se atualiza sozinha.
  const feedQ = useQuery({
    queryKey: FEED_KEY,
    queryFn: () => getSpecialistFeed('noticias'),
    refetchInterval: (query) => (query.state.data?.generating ? 4000 : false),
  });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const entitlementsQ = useEntitlements();
  const pro = !!entitlementsQ.data && planAtLeast(entitlementsQ.data.plan, 'pro');
  const [filter, setFilter] = useState<string>('all');

  const topics = useMemo<NewsTopicId[]>(() => {
    const raw = Array.isArray(feedQ.data?.config?.themes)
      ? (feedQ.data!.config.themes as unknown[])
      : [];
    return [...new Set(raw.map(normalizeNewsTopic).filter((t): t is NewsTopicId => t !== null))];
  }, [feedQ.data]);

  // Só as notícias do dia mais recente separado; as antigas não voltam pra tela.
  const latest = useMemo(() => {
    // Alertas têm seção própria; a edição é só o que foi separado no dia.
    const items = (feedQ.data?.items ?? []).filter((it) => it.kind !== 'alert');
    if (items.length === 0) return [];
    const day = spDay(items[0]!.createdAt);
    return items.filter((it) => spDay(it.createdAt) === day);
  }, [feedQ.data]);

  const hasUnread = latest.some((it) => !it.readAt);
  useEffect(() => {
    if (!hasUnread) return;
    markSpecialistRead('noticias')
      .then(() => qc.invalidateQueries({ queryKey: ['pa-specialists'] }))
      .catch(() => {
        /* só o badge "novos" que fica, sem problema */
      });
  }, [hasUnread, qc]);

  async function save(next: NewsTopicId[]) {
    setSaving(true);
    setEditing(false);
    setFilter('all');
    try {
      await saveNewsTopics(next);
    } finally {
      await qc.invalidateQueries({ queryKey: FEED_KEY });
      setSaving(false);
    }
  }

  if (feedQ.isLoading) {
    return (
      <PageShell kicker="Notícias" title="Suas notícias" onBack={back}>
        <Skeleton variant="rows" label="Abrindo as notícias" />
      </PageShell>
    );
  }

  if (saving || (feedQ.data?.generating && latest.length === 0 && topics.length > 0 && !editing)) {
    return (
      <PageShell
        kicker="Notícias"
        title="Lendo as notícias de hoje"
        lead="Separo o que importa nos seus assuntos e já mostro aqui."
        onBack={back}
      >
        <Skeleton variant="rows" label="Lendo as notícias de hoje" />
      </PageShell>
    );
  }

  if (topics.length === 0 || editing) {
    return (
      <TopicPicker
        initial={topics}
        editing={editing}
        onSave={save}
        onCancel={() => setEditing(false)}
        onBack={back}
      />
    );
  }

  const shown = filter === 'all' ? latest : latest.filter((it) => it.theme === filter);
  const title =
    latest.length === 0
      ? 'Ainda estou lendo as notícias'
      : `Seu dia em ${latest.length} ${latest.length === 1 ? 'notícia' : 'notícias'}`;

  return (
    <PageShell
      kicker="Notícias"
      title={title}
      lead={
        latest[0] ? formatDayLine(latest[0].createdAt) : 'Volta daqui a pouco que eu já separo.'
      }
      onBack={back}
      aside={
        <button type="button" className="pgs-btn small" onClick={() => setEditing(true)}>
          Assuntos
        </button>
      }
    >
      <div className="pgs-chips" role="group" aria-label="Filtrar por assunto">
        <button
          type="button"
          className={`pgs-chip nv-chip${filter === 'all' ? ' on' : ''}`}
          aria-pressed={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          Tudo
        </button>
        {topics.map((t) => (
          <button
            key={t}
            type="button"
            className={`pgs-chip nv-chip${filter === t ? ' on' : ''}`}
            aria-pressed={filter === t}
            onClick={() => setFilter(t)}
          >
            <img src={topicIcon(t)} alt="" />
            {newsTopicLabel(t)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="pgs-note">
          <i aria-hidden="true">i</i>
          <span>
            <b>
              {latest.length === 0
                ? 'Ainda estou lendo as notícias de hoje.'
                : 'Nada desse assunto hoje.'}
            </b>
            <small>
              {latest.length === 0
                ? 'Volta daqui a pouco que eu já separo pra você.'
                : 'Assim que sair algo que importa, eu separo aqui.'}
            </small>
            {latest.length === 0 && (
              <button
                type="button"
                className="pgs-btn small nv-retry"
                onClick={() => qc.invalidateQueries({ queryKey: FEED_KEY })}
              >
                Ver de novo
              </button>
            )}
          </span>
        </div>
      ) : (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            {filter === 'all' ? 'Destaques' : newsTopicLabel(filter)} <small>{shown.length}</small>
          </h2>
          {shown.map((it, index) => (
            <NewsRow key={it.id} item={it} withSummary={index === 0} />
          ))}
        </section>
      )}

      {pro && <NewsProEdition pro />}

      {shown.length > 0 && (
        <>
          {!pro && entitlementsQ.data && <NewsProEdition pro={false} />}

          <NewsAlerts
            topics={
              Array.isArray(feedQ.data?.config?.alerts)
                ? (feedQ.data!.config.alerts as string[])
                : []
            }
            items={(feedQ.data?.items ?? []).filter((it) => it.kind === 'alert')}
          />

          <div className="pgs-note">
            <i aria-hidden="true">›</i>
            <span>
              <b>Amanhã cedo eu volto</b>
              <small>Com uma seleção renovada dos seus assuntos. Resumos feitos com IA.</small>
            </span>
          </div>
        </>
      )}
    </PageShell>
  );
}
